import * as React from 'react';
import { Dispatch, createContext, useReducer } from 'react';

import { assertUnreachable } from '../../../../util/assertUnreachable';

const ShareRoomDialogStateContext =
  createContext<ShareRoomDialogState>(undefined);
const ShareRoomDialogDispatchContext =
  createContext<Dispatch<ShareRoomDialogAction>>(undefined);

type ShareRoomDialogAction =
  | { type: 'Open'; payload: { roomId: number } }
  | { type: 'Close' };

export function dispatchCloseShareRoomDialog(
  dispatch: DispatchShareRoomModalAction
) {
  return () => dispatch({ type: 'Close' });
}

export function dispatchOpenShareRoomModal(
  dispatch: DispatchShareRoomModalAction
) {
  return (roomId: number) =>
    dispatch({ type: 'Open', payload: { roomId: roomId } });
}

type ShareRoomDialogState = { isOpen: boolean; roomId: number };
function shareRoomDialogReducer(
  state: ShareRoomDialogState,
  action: ShareRoomDialogAction
) {
  switch (action.type) {
    case 'Close': {
      return { isOpen: false, roomId: null };
    }
    case 'Open': {
      const { roomId } = action.payload;
      return { isOpen: true, roomId: roomId };
    }
    default: {
      assertUnreachable(action);
    }
  }
}

type DispatchShareRoomModalAction = React.Dispatch<ShareRoomDialogAction>;
export function ShareRoomDialogProvider({
  initialRoomId = null,
  children,
}: {
  initialRoomId: number;
  children: React.ReactNode;
}) {
  const [shareRoomDialogState, dispatch] = useReducer(shareRoomDialogReducer, {
    isOpen: initialRoomId !== null,
    roomId: initialRoomId,
  });

  return (
    <ShareRoomDialogStateContext.Provider value={shareRoomDialogState}>
      <ShareRoomDialogDispatchContext.Provider value={dispatch}>
        {children}
      </ShareRoomDialogDispatchContext.Provider>
    </ShareRoomDialogStateContext.Provider>
  );
}

export function useShareRoomModalState() {
  const context = React.useContext(ShareRoomDialogStateContext);
  if (context === undefined) {
    throw new Error(
      'useShareRoomModalState must be used within a ShareRoomDialogStateContext'
    );
  }
  return context;
}

export function useShareRoomDialogDispatch() {
  const context = React.useContext(ShareRoomDialogDispatchContext);
  if (context === undefined) {
    throw new Error(
      'useShareRoomDialogDispatch must be used within a ShareRoomDialogDispatchContext'
    );
  }
  return context;
}
